import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, Wallet } from "lucide-react";
import PageMeta from "@/components/PageMeta";
import SiteFooter from "@/components/SiteFooter";
import { fmtAud, fmtPct, useSwyftxCredential } from "@/hooks/usePortfolioReport";

interface Holding {
  symbol: string; name: string; balance: number;
  price_aud: number | null; value_aud: number | null;
  change_24h_pct: number | null; change_7d_pct: number | null;
}
type SortKey = "value_aud" | "change_24h_pct" | "change_7d_pct" | "symbol";

const PIE_COLORS = ["hsl(var(--primary))", "hsl(var(--accent))", "hsl(var(--chart-1, 200 80% 55%))", "hsl(var(--chart-2, 160 60% 45%))", "hsl(var(--chart-3, 30 80% 55%))", "hsl(var(--muted-foreground))"];

const pctClass = (n: number | null) => (n == null ? "text-muted-foreground" : n >= 0 ? "text-success" : "text-destructive");

export default function Portfolio() {
  const { user, isLoading: authLoading } = useAuth();
  const { data: cred, isLoading: credLoading } = useSwyftxCredential();
  const loading = authLoading || (!!user && credLoading);
  const [sort, setSort] = useState<SortKey>("value_aud");

  const { data: reports, isLoading } = useQuery({
    queryKey: ["portfolio-dashboard", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("portfolio_reports")
        .select("report_date, status, snapshot, summary, created_at")
        .eq("status", "ready")
        .order("report_date", { ascending: false })
        .limit(90);
      if (error) throw error;
      return data ?? [];
    },
  });

  const latest = reports?.[0] as unknown as { snapshot: { holdings: Holding[]; totalAud: number; fetchedAt?: string } | null; summary: { change_24h_aud: number | null; change_24h_pct: number | null } | null; created_at: string } | undefined;
  const holdings = useMemo(() => {
    const list = [...(latest?.snapshot?.holdings ?? [])];
    return list.sort((a, b) => sort === "symbol" ? a.symbol.localeCompare(b.symbol) : ((b[sort] ?? -Infinity) as number) - ((a[sort] ?? -Infinity) as number));
  }, [latest, sort]);
  const total = latest?.snapshot?.totalAud ?? 0;
  const cash = latest?.snapshot?.holdings.find((h) => h.symbol === "AUD")?.value_aud ?? 0;

  const history = useMemo(() => (reports ?? [])
    .map((r) => ({ date: r.report_date as string, value: (r.snapshot as { totalAud?: number } | null)?.totalAud ?? null }))
    .filter((p) => p.value != null).reverse(), [reports]);

  const allocation = useMemo(() => {
    const sorted = [...(latest?.snapshot?.holdings ?? [])].sort((a, b) => (b.value_aud ?? 0) - (a.value_aud ?? 0));
    const top = sorted.slice(0, 5).map((h) => ({ name: h.symbol, value: h.value_aud ?? 0 }));
    const rest = sorted.slice(5).reduce((s, h) => s + (h.value_aud ?? 0), 0);
    return rest > 0 ? [...top, { name: "Other", value: rest }] : top;
  }, [latest]);

  const shell = (body: React.ReactNode) => (
    <div className="min-h-screen bg-background">
      <PageMeta title="My Portfolio | GoToCoins" description="Your Swyftx holdings, balance and portfolio value over time." path="/portfolio" />
      <div className="container mx-auto px-4 py-8 max-w-6xl space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2"><Wallet className="w-6 h-6 text-primary" /> My Portfolio</h1>
          <Button asChild variant="outline" size="sm"><Link to="/my-report">Morning report <ArrowRight className="w-4 h-4" /></Link></Button>
        </div>
        {body}
      </div>
      <SiteFooter />
    </div>
  );

  if (loading) return shell(<Skeleton className="h-64" />);
  if (!user) return shell(<Card><CardContent className="p-6">Sign in and add your Swyftx read-only key to see your portfolio. <Link className="text-primary underline" to="/auth">Sign in</Link></CardContent></Card>);
  if (!cred) return shell(<Card><CardContent className="p-6">Add your Swyftx read-only key on the <Link className="text-primary underline" to="/account">Account page</Link> to see your portfolio.</CardContent></Card>);
  if (isLoading) return shell(<Skeleton className="h-64" />);
  if (!latest?.snapshot) return shell(<Card><CardContent className="p-6">No portfolio data yet. Run your report from the <Link className="text-primary underline" to="/my-report">My Report page</Link>.</CardContent></Card>);

  return shell(<>
    <p className="text-xs text-muted-foreground">Live Swyftx balances as of {new Date(latest.snapshot.fetchedAt ?? latest.created_at).toLocaleString("en-AU", { timeZone: "Australia/Sydney" })} (Sydney).</p>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[
        ["Total balance", fmtAud(total), null],
        ["24h change", `${fmtAud(latest.summary?.change_24h_aud)} (${fmtPct(latest.summary?.change_24h_pct)})`, latest.summary?.change_24h_pct ?? null],
        ["Cash (AUD)", fmtAud(cash), null],
        ["Holdings", String(holdings.filter((h) => h.symbol !== "AUD").length), null],
      ].map(([label, value, pct]) => (
        <Card key={label as string}><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className={`text-lg font-bold ${pct == null ? "text-foreground" : pctClass(pct as number)}`}>{value}</div>
        </CardContent></Card>
      ))}
    </div>

    <div className="grid md:grid-cols-3 gap-4">
      <Card className="md:col-span-2">
        <CardHeader><CardTitle className="text-base">Portfolio value</CardTitle></CardHeader>
        <CardContent className="h-64">
          {history.length < 2 ? <p className="text-sm text-muted-foreground">The chart fills in as daily reports build up ({history.length} so far).</p> : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tickFormatter={(v) => `A$${Math.round(v / 1000)}k`} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={55} />
                <Tooltip formatter={(v: number) => fmtAud(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }} />
                <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.2)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Allocation</CardTitle></CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={allocation} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>
                {allocation.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v: number) => `${fmtAud(v)} (${total ? ((v / total) * 100).toFixed(1) : 0}%)`} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardHeader><CardTitle className="text-base">Holdings</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b border-border">
            <tr>
              {([["symbol", "Coin"], [null, "Balance"], [null, "Price"], ["value_aud", "Value"], [null, "Share"], ["change_24h_pct", "24h"], ["change_7d_pct", "7d"]] as [SortKey | null, string][]).map(([k, l]) => (
                <th key={l} className={`px-3 py-2 ${l === "Coin" ? "text-left" : "text-right"}`}>
                  {k ? <button onClick={() => setSort(k)} className={sort === k ? "text-foreground font-semibold" : ""}>{l}</button> : l}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {holdings.map((h) => (
              <tr key={h.symbol} className="border-b border-border/50">
                <td className="px-3 py-2"><Link to={`/coin-reviewer?symbol=${h.symbol}`} className="font-semibold text-foreground hover:text-primary">{h.symbol}</Link> <span className="text-xs text-muted-foreground">{h.name}</span></td>
                <td className="px-3 py-2 text-right">{h.balance.toLocaleString("en-AU", { maximumFractionDigits: 4 })}</td>
                <td className="px-3 py-2 text-right">{fmtAud(h.price_aud)}</td>
                <td className="px-3 py-2 text-right font-medium">{fmtAud(h.value_aud)}</td>
                <td className="px-3 py-2 text-right">{total ? `${(((h.value_aud ?? 0) / total) * 100).toFixed(1)}%` : "n/a"}</td>
                <td className={`px-3 py-2 text-right ${pctClass(h.change_24h_pct)}`}>{fmtPct(h.change_24h_pct)}</td>
                <td className={`px-3 py-2 text-right ${pctClass(h.change_7d_pct)}`}>{fmtPct(h.change_7d_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  </>);
}
